import React, { useEffect, useState } from "react";
import { Ban, CalendarX, MoreHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Session } from "../../features/sessions/models/Session";
import { BookingStatus } from "../../features/sessions/models/BookingStatus";
import { RecurrenceType } from "../../features/sessions/models/RecurrenceType";
import SessionLifecycleDialog, { LifecycleDialogKind } from "./SessionLifecycleDialog";

interface SessionActionsMenuProps {
  session: Session;
}

const itemClass =
  "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-grey-10 hover:bg-grey-95 dark:text-grey-90 dark:hover:bg-primary-20";

/**
 * The session header's "more actions" kebab: the whole-session lifecycle
 * actions (end the series, cancel the session) live here, next to Edit, so
 * they are found where every other entity keeps its destructive actions
 * instead of at the bottom of the page.
 */
const SessionActionsMenu: React.FC<SessionActionsMenuProps> = ({ session }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState<LifecycleDialogKind | null>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!(e.target as Element).closest("[data-session-actions-menu]")) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  if (session.status !== BookingStatus.Active) return null;
  const isRecurring = session.recurrenceType !== RecurrenceType.No;

  const pick = (kind: LifecycleDialogKind) => () => {
    setOpen(false);
    setDialog(kind);
  };

  return (
    <div className="relative shrink-0" data-session-actions-menu>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="btn btn-secondary px-3"
        title={t("sessions.lifecycle.menuTitle")}
        aria-label={t("sessions.lifecycle.menuTitle")}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreHorizontal size={18} strokeWidth={2} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-20 mt-2 w-52 rounded-xl border border-grey-90 bg-white p-1 shadow-lg dark:border-primary-20 dark:bg-primary-10"
        >
          {isRecurring && (
            <button type="button" role="menuitem" className={itemClass} onClick={pick("endSeries")}>
              <CalendarX size={16} />
              <span>{t("sessions.lifecycle.endSeries")}</span>
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/30"
            onClick={pick("cancel")}
          >
            <Ban size={16} />
            <span>{t("sessions.lifecycle.cancelSession")}</span>
          </button>
        </div>
      )}

      {dialog && (
        <SessionLifecycleDialog kind={dialog} session={session} onClose={() => setDialog(null)} />
      )}
    </div>
  );
};

export default SessionActionsMenu;
